/**
 * Named radii, in pixels.
 *
 * Playful ("Soft" roundness) defines five roles: chips 9, controls/inputs 8,
 * cards 10, dialogs 12, and buttons/pills 999. The names below predate that —
 * `sm`/`md`/`lg`/`xl`/`xxl` were recorded during the decoupling wave and
 * encoded the magnitudes the app happened to render then (5/10/15/20/25).
 *
 * The Playful flip maps each onto the nearest Playful role rather than
 * inventing a sixth value, which is why several names now share a number:
 *
 *   none  0  → 0            unchanged
 *   sm    5  → 8   control  the small radius is Playful's input radius
 *   md   10  → 10  card     already the card radius; unchanged
 *   lg   15  → 12  dialog   Playful's largest scalar radius
 *   xl   20  → 12  dialog   Playful has nothing between 12 and 999
 *   xxl  25  → 12  dialog   same
 *
 * The t-shirt names are now misleading — `lg`, `xl` and `xxl` are one value.
 * Renaming them to their Playful roles means touching ~130 call sites, so it
 * is deliberately left for a follow-up: this file is the one place the numbers
 * live, and correcting the numbers matters more than correcting the labels.
 *
 * Numeric entries are px magnitudes — a call site wraps them in a template
 * (`` `${radii.lg}px` ``) to get an explicit-unit string, EXCEPT `none`: a
 * bare `borderRadius: 0` and a templated `borderRadius: "0px"` serialize to
 * different emotion classes, so every zero call site uses `radii.none` bare.
 *
 * String entries (`circle` and below) are already complete CSS values, used
 * bare and never templated. The multi-corner strings are shape decorations
 * rather than scalar radii and are unaffected by roundness.
 */
const radii = {
  none: 0,
  /**
   * Playful `chip` role. The header above already documented "chips 9" as
   * one of the five roles; this is the first non-`Override.tsx` call site
   * that needs the number itself (the auth card-level error banner), so it
   * gets its own named slot here rather than a second private constant.
   */
  chip: 9,
  /** Playful `control` — inputs and small controls. */
  sm: 8,
  /** Playful `card`. */
  md: 10,
  /** Playful `dialog`. */
  lg: 12,
  /** Toast severity `Paper` / invite `Chip` border-color / header menu. Playful `dialog`. */
  xl: 12,
  /** Hub engagement `Paper`, project-role `Chip`, note `Card`s. Playful `dialog`. */
  xxl: 12,
  /**
   * `.btn-main` primary submit button (BuildersQuillForm save, Members
   * submitButtonSx). Playful buttons are pills, so this is 999 rather than a
   * small radius — the same change `Override.tsx` makes to `MuiButton`.
   */
  submitButton: 999,
  /** Floating panel containers — react-modal box (`modalCss.ts`), `RebelMap`. Playful `card`. */
  panel: 10,
  /** `Pages/NoMatch` scrim box. Playful `card`. */
  noMatchScrim: 10,
  circle: "50%",
  /** Raised `Card`s: member grid card, activity/question reply cards, activity-tile skeleton. */
  raisedCard: "10px",
  /** `HubMember` `.member-card__logo`. */
  hubMemberCardLogo: "5%",
  /** `Members` `.member-card__profile-img`. */
  memberCardAvatar: "100%",
  /** `Members` `.member-card__summary` base wedge. */
  memberCardSummaryBase: "50% 0 0 0",
  /** `Members` `.member-card__summary` — rebel variant. */
  memberCardSummaryRebel: "35% 0 0 0",
  /** `Members` `.member-card__summary` — admin variant. */
  memberCardSummaryAdmin: "15% 0 0 0",
  /** `Members` `.member-card__summary` — judge variant. */
  memberCardSummaryJudge: "55% 0 0 0",
  /** `Members` `.member-card__summary` — partner and general variants (shared). */
  memberCardSummaryPartner: "30% 0 0 0",
  /** `HubMember` `.member-card__summary` base wedge. */
  hubMemberCardSummaryBase: "25% 0 0 0",
} as const;

export default radii;
